import { hasDefinedValue, isNotDefined } from '@sgme/fp';
import { missedDealFeedbackConnector } from 'apiConnector/missed-deal-feedback';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { ofType } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import {
  missedDealFeedbackSubmissionRequestFailed,
  missedDealFeedbackSubmitted,
} from 'state/missed-deal-feeedback/actions';
import {
  getCompetitionParametersFeedbacks,
  getFeedback,
  getFeedbackIsClientTraded,
  getFeedbackNumberOfIssuers,
  getFeedbackSgPosition,
  getUserFeedbackPriceEnhancement,
} from 'state/missed-deal-feeedback/selectors';
import type { EpicWithThunk } from 'state/state.common';

export interface FeedbackPost {
  pricing_id: string;
  comment?: string;
  sgPosition?: number;
  numberOfIssuers?: number;
  isClientTraded?: boolean;
  competition_parameters?: {
    label: string;
    value: number;
    is_annualised: boolean;
    is_percent: boolean;
  }[];
  priceEnhancement?: {
    issuePrice: number;
    xMargin: number;
    tradingOffer: number;
  };
  withAdditionalInformation: boolean;
}

export function submitFeedbackParameterEpic(): EpicWithThunk {
  return (action$, state$, { oldHttpClient, frontLogger }) => {
    return action$.pipe(
      ofType('MISSED_DEAL_FEEDBACK_SUBMISSION_REQUEST_CONFIRMED'),
      withLatestFrom(state$),
      mergeMap(([{ tabId }, state]) => {
        const feedback = getFeedback(state, tabId);
        let payoff: FeedbackPost;
        if (isNotDefined(feedback?.pricerId)) {
          return EMPTY;
        }
        if (feedback.withAdditionalInfo === false) {
          payoff = { pricing_id: feedback.pricerId, withAdditionalInformation: false, comment: feedback.comment };
        } else {
          payoff = {
            pricing_id: feedback.pricerId,
            withAdditionalInformation: true,
            comment: feedback.comment,
            sgPosition: getFeedbackSgPosition(state, tabId),
            numberOfIssuers: getFeedbackNumberOfIssuers(state, tabId),
            isClientTraded: getFeedbackIsClientTraded(state, tabId),
            priceEnhancement: getUserFeedbackPriceEnhancement(state, tabId),
            competition_parameters: getCompetitionParametersFeedbacks(state, tabId)
              ?.filter(param => hasDefinedValue('label')(param) && hasDefinedValue('bestOfferValue')(param))
              .map(p => {
                return {
                  label: p.label,
                  value: p.bestOfferValue,
                  is_annualised: p.bestOfferFrequency === 'PA',
                  is_percent: true,
                };
              }),
          };
        }

        return missedDealFeedbackConnector(oldHttpClient)
          .submitFeedback(payoff)
          .pipe(
            map(ack => missedDealFeedbackSubmitted(tabId, ack)),
            catchError((error: Error) => {
              frontLogger.error('An error occurred in submitFeedbackParameterEpic {@FrontError}', error);

              let apiErrorMessage = { message: 'missedDealFeedback.generic.error.message' };

              if (apiErrorHelper.isHttpTimeoutError(error)) {
                apiErrorMessage = { message: 'missedDealFeedback.submit.timeoutWarning' };
              }

              return of(missedDealFeedbackSubmissionRequestFailed(tabId, apiErrorMessage, error));
            }),
          );
      }),
    );
  };
}
